import { onCLS, onINP, onFCP, onLCP, onTTFB } from 'web-vitals'
import { isPrivateRoute } from '@/lib/private-routes'

/**
 * Report Core Web Vitals metrics
 * This function can be customized to send metrics to your analytics service
 */
function sendToAnalytics(metric: any) {
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log('Core Web Vital:', metric)
  }

  // In production, you would send this to your analytics service
  // Examples:
  // - Google Analytics 4
  // - Vercel Analytics
  // - Custom analytics endpoint

  if (typeof window !== 'undefined' && (window as any).gtag) {
    if (isPrivateRoute(window.location.pathname))
      return // Send to Google Analytics if available
    ;(window as any).gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(
        metric.name === 'CLS' ? metric.value * 1000 : metric.value
      ),
      non_interaction: true,
    })
  }
}

/**
 * Initialize Core Web Vitals reporting
 * Call this function in your app to start measuring performance metrics
 */
export function reportWebVitals() {
  // Only run in browser environment
  if (typeof window === 'undefined') return

  try {
    // Cumulative Layout Shift - measures visual stability
    onCLS(sendToAnalytics)

    // Interaction to Next Paint - measures interactivity (replaces FID)
    onINP(sendToAnalytics)

    // First Contentful Paint - measures loading performance
    onFCP(sendToAnalytics)

    // Largest Contentful Paint - measures loading performance
    onLCP(sendToAnalytics)

    // Time to First Byte - measures server response time
    onTTFB(sendToAnalytics)
  } catch (error) {
    console.error('Error initializing web vitals:', error)
  }
}

/**
 * Performance monitoring utility for custom metrics
 */
export class PerformanceMonitor {
  private static marks: Map<string, number> = new Map()

  /**
   * Start measuring a custom performance metric
   */
  static start(name: string) {
    if (typeof performance !== 'undefined') {
      const startTime = performance.now()
      this.marks.set(name, startTime)
      performance.mark(`${name}-start`)
    }
  }

  /**
   * End measuring a custom performance metric and report it
   */
  static end(name: string) {
    if (typeof performance !== 'undefined') {
      const endTime = performance.now()
      const startTime = this.marks.get(name)

      if (startTime) {
        const duration = endTime - startTime
        performance.mark(`${name}-end`)
        performance.measure(name, `${name}-start`, `${name}-end`)

        // Report custom metric
        sendToAnalytics({
          name: `custom-${name}`,
          value: duration,
          id: `custom-${Date.now()}`,
        })

        this.marks.delete(name)
      }
    }
  }
}
